/**
 * Derivar un tono claro del color de marca de la compañía.
 *
 * No sirve `theme.token.colorPrimaryBg` ni `Menu.itemSelectedBg`: cuando la base
 * de datos no los define, el hook los deja en el token BASE de antd —un azul—
 * aunque `colorPrimary` sí venga personalizado. Usarlos pintaría de azul el
 * realce de una compañía teal.
 *
 * Tampoco se usa `color-mix()` en CSS: todavía no está en el rango de
 * navegadores que compila el proyecto.
 */

/** `#0BF`, `#00BFB3` o `rgb(0, 191, 179)` → `[r, g, b]`. */
const parseColor = (input: string): [number, number, number] | null => {
  const value = input.trim();

  const rgb = value.match(/^rgba?\(([^)]+)\)$/i);
  if (rgb) {
    const parts = rgb[1].split(',').map((p) => parseFloat(p));
    if (parts.length >= 3 && parts.slice(0, 3).every((n) => !Number.isNaN(n))) {
      return [parts[0], parts[1], parts[2]];
    }
    return null;
  }

  const hex = value.replace('#', '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;

  if (!/^[0-9a-f]{6}$/i.test(full)) return null;

  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
};

/**
 * Mezcla el color con blanco y devuelve un color SÓLIDO, no un `rgba`: sobre una
 * fila de tabla, una capa translúcida se combina con el hover de antd y da un
 * tono distinto al esperado.
 *
 * @param weight cuánto del color original conserva (0–1).
 */
export const tintWithWhite = (
  color: string | undefined,
  weight = 0.1,
  fallback = '#E6FFFB'
): string => {
  if (!color) return fallback;

  const parsed = parseColor(color);
  if (!parsed) return fallback;

  const mix = (channel: number) =>
    Math.round(channel * weight + 255 * (1 - weight));

  return `rgb(${mix(parsed[0])}, ${mix(parsed[1])}, ${mix(parsed[2])})`;
};
